import apiClient from "../../../utility/axios";

function buildQueryParams({
  questionId,
  filters = {},
  selectedCategory,
  limit,
  tag_type,
  seed,
}) {
  const queryParams = new URLSearchParams();
  if (questionId) queryParams.append("question_id", questionId);
  if (filters.demographics && typeof filters.demographics === "object") {
    Object.entries(filters.demographics).forEach(([group, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          queryParams.append(group, value);
        });
      }
    });
  }
  if (filters.tags && filters.tags.length > 0) {
    filters.tags.forEach((tag) => queryParams.append("tag", tag));
  }
  // Date filter logic
  if (filters.dateFilter) {
    if (filters.dateFilter.type === "range") {
      if (filters.dateFilter.startDate)
        queryParams.append("start_date", filters.dateFilter.startDate);
      if (filters.dateFilter.endDate)
        queryParams.append("end_date", filters.dateFilter.endDate);
    } else if (filters.dateFilter.type === "month") {
      if (filters.dateFilter.months && filters.dateFilter.months.length > 0) {
        filters.dateFilter.months.forEach((m) =>
          queryParams.append("month", m)
        );
      }
    }
  }
  if (selectedCategory !== undefined)
    queryParams.append("tag", selectedCategory);
  if (limit !== undefined) queryParams.append("limit", limit);
  if (tag_type !== undefined) queryParams.append("tag_type", tag_type);
  if (seed !== undefined) queryParams.append("seed", seed);
  return queryParams.toString();
}

export const fetchClosedEndedData = async (
  questionId,
  filters = {},
  options = {}
) => {
  const query = buildQueryParams({ questionId, filters });
  const axiosOptions = {};
  if (options.signal) {
    axiosOptions.signal = options.signal;
  }
  const response = await apiClient.get(
    `/api/civic-pulse/closed-end?${query}`,
    axiosOptions
  );
  return response.data.data;
};

export const fetchOpenEndedData = async (
  questionId,
  selectedCategory,
  filters = {},
  limit = 10,
  seed
) => {
  const query = buildQueryParams({
    questionId,
    selectedCategory,
    filters,
    limit,
    seed,
    tag_type: "Theme",
  });
  const response = await apiClient.get(`/api/civic-pulse/open-ends?${query}`);
  return response.data.data;
};

export const fetchCategorizedOpenEndedData = async (
  questionId,
  filters = {},
  options = {}
) => {
  const query = buildQueryParams({ questionId, filters, tag_type: "Theme" });
  const axiosOptions = {};
  if (options.signal) {
    axiosOptions.signal = options.signal;
  }
  const response = await apiClient.get(
    `/api/civic-pulse/count-labeled-open-ends?quality=all&${query}`,
    axiosOptions
  );
  return response.data.data;
};

/**
 * Fetch matrix data for a set of closed-ended questions and format for HorizontalStackedBarChart.
 * @param {string[]} questionIds - Array of question IDs (e.g., ["q11_1", ...])
 * @param {string[]} rowLabels - Array of row labels (same order as questionIds)
 * @param {object} filters - Filters to apply
 * @param {string[]} [categories] - Optional array of categories to dictate display order
 * @returns {Promise<{ data: object, categories: string[] }>} - { data, categories }
 */
export const fetchMatrixData = async (
  questionIds,
  rowLabels,
  filters = {},
  categories,
  options = {}
) => {
  // Fetch all closed-ended data in parallel
  const results = await Promise.all(
    questionIds.map((qid) => fetchClosedEndedData(qid, filters, options))
  );
  // Use provided categories or infer from the first question's responses
  const usedCategories =
    categories && categories.length > 0
      ? categories
      : results[0]?.map((d) => d.response) || [];
  // Format data for HorizontalStackedBarChart
  // { rowLabel: { category: value, ... }, ... }
  const data = {};
  results.forEach((responses, i) => {
    const row = rowLabels[i] || questionIds[i];
    data[row] = {};
    usedCategories.forEach((cat) => {
      const found = responses.find((r) => r.response === cat);
      data[row][cat] = found ? found.weighted_count : 0;
    });
  });
  return { data, categories: usedCategories };
};

export const fetchUniverseSize = async (filters = {}, options = {}) => {
  const query = buildQueryParams({ filters });
  const axiosOptions = {};
  if (options.signal) {
    axiosOptions.signal = options.signal;
  }
  const response = await apiClient.get(
    `/api/civic-pulse/universe?${query}`,
    axiosOptions
  );
  return response.data.data;
};
